import { useState } from 'react'
import { Link } from 'react-router-dom'
import { SEOHead } from '@/components/layout/SEOHead'
import { graph, organization, breadcrumbList, faqPage, type FaqItem } from '@/lib/jsonLd'

/**
 * Landing page that promotes the embeddable calculator widget (/embed) to
 * Polish finance/property bloggers, doradcy kredytowi, and media outlets.
 *
 * SEO purpose: every blogger who embeds the iframe leaves an attribution link
 * to RatyGo.pl — this is currently the highest-leverage free backlink channel
 * available (no Prowly, no defunct directories). Page targets the keyword
 * "kalkulator kredytu hipotecznego na stronę" + variants.
 *
 * Why a public landing page (not just docs): bloggers Google for "darmowy
 * kalkulator do osadzenia" — they need to find this themselves. The page must
 * rank.
 */

const EMBED_URL = 'https://ratygo.pl/embed'

const EMBED_CODE = `<iframe
  src="${EMBED_URL}"
  width="100%"
  height="750"
  style="border:0; border-radius:12px; max-width:100%;"
  loading="lazy"
  title="Kalkulator kredytu hipotecznego — RatyGo"
></iframe>`

const FAQS: FaqItem[] = [
  {
    question: 'Czy widget kalkulatora RatyGo jest naprawdę bezpłatny?',
    answer:
      'Tak, widget jest w pełni bezpłatny — bez opłat jednorazowych, abonamentów ani limitów wyświetleń. Jedynym warunkiem jest pozostawienie linku atrybucyjnego do RatyGo.pl, który wbudowany jest w stopkę widgetu.',
  },
  {
    question: 'Na jakich stronach mogę osadzić kalkulator?',
    answer:
      'Widget działa na każdej stronie obsługującej element <iframe> — WordPress, Wix, Squarespace, Joomla, Drupal, Webflow, Ghost, statyczne strony HTML i własne aplikacje. W WordPressie wystarczy blok HTML, w Wix blok Embed Code.',
  },
  {
    question: 'Czy widget działa na urządzeniach mobilnych?',
    answer:
      'Tak. Kalkulator jest w pełni responsywny — automatycznie dopasowuje się do szerokości kontenera, w którym jest osadzony. Sliderami można sterować zarówno myszą jak i dotykiem na ekranie smartfona.',
  },
  {
    question: 'Czy mogę osadzić widget na stronie komercyjnej lub w portalu medialnym?',
    answer:
      'Tak. Widget można osadzić na dowolnej stronie — blogu osobistym, portalu informacyjnym, stronie biura doradztwa kredytowego, kanale YouTube z opisem czy newsletterze. Bez ograniczeń komercyjnych.',
  },
  {
    question: 'Czy mogę usunąć link do RatyGo z widgetu?',
    answer:
      'Nie. Link atrybucyjny do RatyGo.pl w stopce widgetu jest wymagany przy każdym osadzeniu. Możesz natomiast zmienić tekst poprzedzający link — patrz parametr ?attr= w sekcji "Dostosowanie".',
  },
  {
    question: 'Czy widget obsługuje język angielski lub inne języki?',
    answer:
      'Tak — widget jest dostępny w 7 językach: polski (domyślnie), angielski (?lang=en), francuski (?lang=fr), hiszpański (?lang=es), niemiecki (?lang=de), rosyjski (?lang=ru) i ukraiński (?lang=uk). Dla innych języków tekst atrybucji w stopce możesz dodatkowo dostosować przez parametr ?attr=.',
  },
  {
    question: 'Czy mogę zmienić walutę z PLN na euro, dolary albo inną?',
    answer:
      'Tak — dodaj parametr ?currency=<ISO> do URL widgetu (np. ?currency=EUR, ?currency=USD, ?currency=GBP). Działa dowolny 3-literowy kod ISO 4217. UWAGA: parametr zmienia tylko sposób wyświetlania liczb — domyślne oprocentowanie 7,85% odpowiada polskiemu rynkowi (WIBOR plus marża). W strefie euro typowe oprocentowanie jest niższe (ok. 3,5%), w USA i UK podobnie ok. 6-7% — czytelnik powinien dostosować suwak Oprocentowanie do swojego rynku.',
  },
  {
    question: 'Czy mogę zmienić tekst atrybucji "Kalkulator dostarczony przez"?',
    answer:
      'Tak — dodaj parametr ?attr=<własny+tekst> do URL widgetu. Przykład: ?attr=Powered+by daje "Powered by RatyGo.pl". Sam link RatyGo.pl pozostaje zawsze widoczny — możesz dostosować jedynie tekst poprzedzający. Limit 60 znaków.',
  },
  {
    question: 'Czy widget spowalnia ładowanie mojej strony?',
    answer:
      'Nie. Widget używa atrybutu loading="lazy" — ładuje się dopiero gdy użytkownik przewinie stronę do miejsca, w którym jest osadzony. Strona główna ładuje się bez czekania na kalkulator.',
  },
  {
    question: 'Czy widget zbiera dane użytkowników mojej strony?',
    answer:
      'Nie. Wszystkie obliczenia odbywają się lokalnie w przeglądarce — żadne parametry kredytu nie są wysyłane na serwer RatyGo ani gdziekolwiek indziej. Widget korzysta z anonimowych statystyk Google Analytics tylko do mierzenia ogólnej popularności narzędzia.',
  },
  {
    question: 'Jak zgłosić błąd lub zaproponować funkcję widgetu?',
    answer:
      'Pisz na ratygoinfo@gmail.com. Odpowiadamy w ciągu 1-2 dni roboczych. Sugestie funkcji rozpatrywane są na bieżąco — jeśli więcej osób prosi o tę samą funkcję, dodajemy ją w pierwszej kolejności.',
  },
]

const LANDING_JSONLD = graph(
  {
    '@type': 'WebPage',
    '@id': 'https://ratygo.pl/widget-hipoteczny#webpage',
    name: 'Wstaw bezpłatny kalkulator kredytu hipotecznego na swoją stronę',
    description:
      'Darmowy widget kalkulatora kredytu hipotecznego do osadzenia na blogu, w artykule lub w sidebarze. Polskie liczby, responsywny, bez kosztów i bez kont.',
    url: 'https://ratygo.pl/widget-hipoteczny',
    inLanguage: 'pl',
    isPartOf: { '@id': 'https://ratygo.pl#organization' },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: 'https://ratygo.pl/og-image.png',
      width: 1200,
      height: 630,
    },
  },
  {
    '@type': 'HowTo',
    '@id': 'https://ratygo.pl/widget-hipoteczny#howto',
    name: 'Jak osadzić kalkulator kredytu hipotecznego na stronie',
    description:
      'Trzy kroki do osadzenia darmowego kalkulatora kredytu hipotecznego RatyGo na dowolnej stronie internetowej.',
    totalTime: 'PT2M',
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Skopiuj kod iframe',
        text: 'Skopiuj fragment kodu HTML <iframe> ze strony RatyGo.pl/widget-hipoteczny. Kod ma około 7 linii.',
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Wklej kod na swojej stronie',
        text: 'Wklej skopiowany kod w miejscu, w którym ma pojawić się kalkulator — w WordPress użyj bloku HTML, w Wix bloku Embed Code, w Ghost bloku HTML.',
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Opublikuj stronę',
        text: 'Zapisz i opublikuj stronę. Kalkulator pojawi się automatycznie i będzie gotowy do użycia przez Twoich czytelników.',
      },
    ],
  },
  organization(),
  breadcrumbList([
    { name: 'Strona główna', url: '/' },
    { name: 'Widget hipoteczny', url: '/widget-hipoteczny' },
  ]),
  faqPage(FAQS),
)

export default function EmbedLandingPage() {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(EMBED_CODE)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API blocked (insecure context, permission denied). Fall back
      // to a textarea-select approach so the code stays copyable.
      const textarea = document.createElement('textarea')
      textarea.value = EMBED_CODE
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <SEOHead
        title="Darmowy widget kalkulatora kredytu hipotecznego na stronę"
        description="Darmowy widget kalkulatora kredytu hipotecznego do osadzenia na blogu lub w artykule. Polskie liczby, responsywny, bez kosztów. WordPress, Wix, Squarespace."
        slug="/widget-hipoteczny"
        keywords={[
          'kalkulator kredytu hipotecznego na stronę',
          'widget kalkulatora hipotecznego',
          'darmowy kalkulator do osadzenia',
          'iframe kalkulator kredytu',
          'kalkulator kredytu na blog',
        ]}
        jsonLd={LANDING_JSONLD}
      />

      <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
        <Link to="/" className="hover:text-foreground transition-colors">
          RatyGo
        </Link>
        <span>/</span>
        <span className="text-foreground">Widget hipoteczny</span>
      </nav>

      <article>
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground leading-tight mb-3">
            Wstaw bezpłatny kalkulator kredytu hipotecznego na swoją stronę
          </h1>
          <p className="text-lg text-muted-foreground">
            Twoi czytelnicy obliczą ratę, koszt całkowity i porównają raty równe z malejącymi
            bezpośrednio w Twoim artykule. Bezpłatnie, bez kont i bez instalacji wtyczek.
          </p>
        </header>

        <div className="space-y-8 text-foreground leading-relaxed">
          <section className="rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-100 p-6">
            <h2 className="text-xl font-bold text-foreground mb-3">Dlaczego warto osadzić kalkulator?</h2>
            <ul className="space-y-2 text-[0.95rem]">
              <li className="flex gap-2"><span className="text-primary font-bold">✓</span> Czytelnicy zostają na Twojej stronie dłużej — kalkulator angażuje bez przekierowania.</li>
              <li className="flex gap-2"><span className="text-primary font-bold">✓</span> Działa wszędzie — WordPress, Wix, Squarespace, Joomla, Ghost, własne strony HTML.</li>
              <li className="flex gap-2"><span className="text-primary font-bold">✓</span> Responsywny — automatycznie dopasowuje się do szerokości kontenera.</li>
              <li className="flex gap-2"><span className="text-primary font-bold">✓</span> Lazy-load — nie spowalnia ładowania Twojej strony.</li>
              <li className="flex gap-2"><span className="text-primary font-bold">✓</span> Bezpłatny na zawsze — bez opłat, bez abonamentów, bez limitu wyświetleń.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Jak osadzić kalkulator w 3 krokach</h2>
            <ol className="space-y-4">
              <li className="flex gap-4">
                <span className="shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center text-sm">1</span>
                <div>
                  <p className="font-semibold text-foreground">Skopiuj kod iframe</p>
                  <p className="text-muted-foreground text-[0.95rem] mt-1">
                    Kliknij przycisk &bdquo;Kopiuj kod&rdquo; poniżej.
                  </p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center text-sm">2</span>
                <div>
                  <p className="font-semibold text-foreground">Wklej kod na swojej stronie</p>
                  <p className="text-muted-foreground text-[0.95rem] mt-1">
                    W WordPress użyj bloku &bdquo;HTML własny&rdquo;, w Wix bloku &bdquo;Embed Code&rdquo;,
                    w Ghost bloku &bdquo;HTML&rdquo;. Na statycznych stronach po prostu wklej między tagi.
                  </p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center text-sm">3</span>
                <div>
                  <p className="font-semibold text-foreground">Opublikuj stronę</p>
                  <p className="text-muted-foreground text-[0.95rem] mt-1">
                    Kalkulator pojawi się automatycznie. Twoi czytelnicy mogą zacząć z niego korzystać natychmiast.
                  </p>
                </div>
              </li>
            </ol>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Kod do skopiowania</h2>
            <div className="relative">
              <pre className="bg-slate-900 text-slate-100 rounded-xl p-4 sm:p-5 overflow-x-auto text-xs sm:text-sm font-mono leading-relaxed border border-slate-800">
                <code>{EMBED_CODE}</code>
              </pre>
              <button
                type="button"
                onClick={handleCopy}
                className={`absolute top-3 right-3 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  copied
                    ? 'bg-emerald-500 text-white'
                    : 'bg-white text-slate-900 hover:bg-slate-100'
                }`}
                aria-label={copied ? 'Skopiowano' : 'Kopiuj kod'}
              >
                {copied ? '✓ Skopiowano' : 'Kopiuj kod'}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Domyślna wysokość 750px działa dla większości artykułów. Możesz dostosować
              <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">width</code> i
              <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">height</code> do swojego layoutu.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Podgląd na żywo</h2>
            <p className="text-muted-foreground text-[0.95rem] mb-4">
              Tak będzie wyglądał kalkulator na Twojej stronie:
            </p>
            <iframe
              src="/embed"
              width="100%"
              height="750"
              style={{ border: 0, borderRadius: 12, maxWidth: '100%' }}
              loading="lazy"
              title="Podgląd kalkulatora kredytu hipotecznego RatyGo"
              className="shadow-sm"
            />
            <div className="mt-4 rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-100 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="font-semibold text-foreground text-[0.95rem]">
                  Chcesz wypróbować różne języki, waluty i wymiary?
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Skonfiguruj widget w kreatorze i skopiuj gotowy kod jednym kliknięciem.
                </p>
              </div>
              <Link
                to="/widget-hipoteczny/kreator"
                className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-foreground text-background text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                Otwórz kreator widgetu →
              </Link>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Dostosowanie — parametry URL</h2>
            <p className="text-[0.95rem]">
              Kalkulator można dostosować bez modyfikacji kodu — wystarczy dodać parametry do
              adresu URL w atrybucie <code className="text-xs bg-muted px-1 py-0.5 rounded">src</code>{' '}
              iframe&apos;a.
            </p>

            <h3 className="text-base font-bold text-foreground mt-5 mb-2">
              <code className="text-sm bg-muted px-1.5 py-0.5 rounded">?lang=&lt;kod&gt;</code> — język interfejsu
            </h3>
            <p className="text-muted-foreground text-[0.95rem]">
              Przełącza interfejs kalkulatora na wybrany język. Obsługiwane kody:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2 text-[0.95rem]">
              <li><code className="text-xs bg-muted px-1 py-0.5 rounded">pl</code> — polski (domyślnie)</li>
              <li><code className="text-xs bg-muted px-1 py-0.5 rounded">en</code> — English</li>
              <li><code className="text-xs bg-muted px-1 py-0.5 rounded">fr</code> — français</li>
              <li><code className="text-xs bg-muted px-1 py-0.5 rounded">es</code> — español</li>
              <li><code className="text-xs bg-muted px-1 py-0.5 rounded">de</code> — Deutsch</li>
              <li><code className="text-xs bg-muted px-1 py-0.5 rounded">ru</code> — русский</li>
              <li><code className="text-xs bg-muted px-1 py-0.5 rounded">uk</code> — українська</li>
            </ul>
            <p className="text-xs text-muted-foreground mt-2">
              Domyślną walutę PLN można zmienić parametrem{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">?currency=</code> (patrz niżej).
              Sama kalkulacja oparta jest na polskim rynku (WIBOR, Rekomendacja S KNF, podatek PCC) —
              użytkownik powinien dostosować suwak <em>Oprocentowanie</em> do swojego rynku.
            </p>
            <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 mt-2 overflow-x-auto text-xs font-mono">
              <code>{`<iframe src="${EMBED_URL}?lang=de" width="100%" height="750"></iframe>`}</code>
            </pre>

            <h3 className="text-base font-bold text-foreground mt-5 mb-2">
              <code className="text-sm bg-muted px-1.5 py-0.5 rounded">?attr=&lt;tekst&gt;</code> — własna atrybucja
            </h3>
            <p className="text-muted-foreground text-[0.95rem]">
              Zastępuje domyślny tekst &bdquo;Kalkulator dostarczony przez&rdquo; w stopce
              kalkulatora. Sam link do <strong>RatyGo.pl</strong> pozostaje zawsze widoczny —
              można dostosować jedynie tekst poprzedzający. Działa w dowolnym języku (np.
              cyrylica, znaki diakrytyczne). Maksymalnie 60 znaków.
            </p>
            <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 mt-2 overflow-x-auto text-xs font-mono">
              <code>{`<iframe src="${EMBED_URL}?attr=Powered+by" width="100%" height="750"></iframe>`}</code>
            </pre>
            <p className="text-xs text-muted-foreground mt-2">
              Wynik w stopce: <em>Powered by <strong>RatyGo.pl</strong></em>
            </p>

            <h3 className="text-base font-bold text-foreground mt-5 mb-2">
              <code className="text-sm bg-muted px-1.5 py-0.5 rounded">?currency=&lt;ISO&gt;</code> — waluta wyświetlania
            </h3>
            <p className="text-muted-foreground text-[0.95rem]">
              Zmienia symbol i format waluty na wszystkich wartościach kalkulatora. Akceptuje
              dowolny 3-literowy kod ISO 4217. Domyślnie <code className="text-xs bg-muted px-1 py-0.5 rounded">PLN</code>.
              Najczęstsze:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2 text-[0.95rem]">
              <li><code className="text-xs bg-muted px-1 py-0.5 rounded">PLN</code> — 500 000 zł (domyślnie)</li>
              <li><code className="text-xs bg-muted px-1 py-0.5 rounded">EUR</code> — 500.000 €</li>
              <li><code className="text-xs bg-muted px-1 py-0.5 rounded">USD</code> — $500,000</li>
              <li><code className="text-xs bg-muted px-1 py-0.5 rounded">GBP</code> — £500,000</li>
              <li><code className="text-xs bg-muted px-1 py-0.5 rounded">CHF</code>, <code className="text-xs bg-muted px-1 py-0.5 rounded">CZK</code>, <code className="text-xs bg-muted px-1 py-0.5 rounded">HUF</code>, <code className="text-xs bg-muted px-1 py-0.5 rounded">RON</code>, <code className="text-xs bg-muted px-1 py-0.5 rounded">RUB</code>, <code className="text-xs bg-muted px-1 py-0.5 rounded">UAH</code> i inne ISO 4217</li>
            </ul>
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <strong>Uwaga:</strong> parametr zmienia wyłącznie sposób wyświetlania liczb. Domyślne
              wartości suwaków (cena 500 000, oprocentowanie 7,85%) odpowiadają polskiemu rynkowi.
              W innych krajach typowe oprocentowanie kredytów hipotecznych jest niższe (np. ok. 3,5% w strefie euro) —
              użytkownik powinien dostosować suwak <em>Oprocentowanie</em> do swojego rynku.
            </div>
            <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 mt-3 overflow-x-auto text-xs font-mono">
              <code>{`<iframe src="${EMBED_URL}?lang=de&currency=EUR" width="100%" height="750"></iframe>`}</code>
            </pre>

            <h3 className="text-base font-bold text-foreground mt-5 mb-2">Wszystkie parametry razem</h3>
            <p className="text-muted-foreground text-[0.95rem]">
              Parametry można dowolnie łączyć. Przykład: hiszpański interfejs, waluta euro,
              własny tekst atrybucji.
            </p>
            <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 mt-2 overflow-x-auto text-xs font-mono">
              <code>{`<iframe src="${EMBED_URL}?lang=es&currency=EUR&attr=Calculadora+de" width="100%" height="750"></iframe>`}</code>
            </pre>
            <p className="text-xs text-muted-foreground mt-2">
              W parametrze URL spacje koduj jako <code className="text-xs bg-muted px-1 py-0.5 rounded">+</code>{' '}
              lub <code className="text-xs bg-muted px-1 py-0.5 rounded">%20</code>. Znaki specjalne,
              cyrylica i znaki diakrytyczne są przekodowywane automatycznie przez przeglądarkę.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Warunki użycia</h2>
            <ul className="list-disc pl-6 space-y-2 text-[0.95rem]">
              <li>
                <strong>Bezpłatnie na zawsze</strong> — bez opłat jednorazowych, bez abonamentów,
                bez limitów wyświetleń.
              </li>
              <li>
                <strong>Wymagana atrybucja</strong> — link do RatyGo.pl w stopce widgetu pozostaje
                widoczny i nie może być ukrywany przez CSS.
              </li>
              <li>
                <strong>Dozwolone wszędzie</strong> — blogi prywatne, portale informacyjne, strony
                biur doradczych, kanały YouTube, newslettery.
              </li>
              <li>
                <strong>Bez modyfikacji wyglądu</strong> — proszę nie wstrzykiwać CSS zmieniającego
                kolory, fonty ani layout. Jeśli potrzebujesz innego wariantu — napisz, rozważymy
                wariant białej etykiety.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Najczęściej zadawane pytania</h2>
            <div className="space-y-5">
              {FAQS.map((faq) => (
                <div key={faq.question}>
                  <h3 className="font-semibold text-foreground mb-1.5">{faq.question}</h3>
                  <p className="text-muted-foreground text-[0.95rem]">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-100">
            <h2 className="text-lg font-bold text-foreground mb-2">Masz pytanie albo specjalne wymagania?</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Potrzebujesz innej domyślnej waluty, własnego loga (white-label) albo integracji z
              Twoim CRM? Napisz, omówimy szczegóły.
            </p>
            <a
              href="mailto:ratygoinfo@gmail.com?subject=Widget%20kalkulatora%20-%20wsp%C3%B3%C5%82praca"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-foreground text-background text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              ratygoinfo@gmail.com
            </a>
          </section>

          <div className="mt-8">
            <Link
              to="/"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
              Wróć do kalkulatora
            </Link>
          </div>
        </div>
      </article>
    </div>
  )
}
